import { Box } from "@mui/material"
import { CheckCircleFilledRounded, DoNotDisturbOnFilledRounded } from "@mui-symbols-material/w300"
import type { CellContext, ColumnDef, Row } from "@tanstack/react-table"

import { PriceUomColumn } from "./PriceUomColumn"
import { PriceUomColumnReadOnly } from "./read-only/PriceUomColumnReadOnly"

import Chip from "@/components/common/Chip/Chip"
import { ProductStatusCell } from "@/components/table"
import type { Order, OrderLine, ProductVersionInfo, UnitOfMeasurement } from "@/graphql/codegen/graphql"
import { LeadTimeUnits } from "@/graphql/codegen/graphql"
import { ActionsCell } from "@/screens/Procurement/PurchaseOrder/ProductAdded/components/ActionsCell"
import { OrderQuantityColumn } from "@/screens/Procurement/PurchaseOrder/ProductAdded/components/OrderQuantityColumn"
import { OrderQuantityColumnReadOnly } from "@/screens/Procurement/PurchaseOrder/ProductAdded/components/read-only/OrderQuantityColumnReadOnly"
import type { PricingOrderLine } from "@/screens/Procurement/PurchaseOrder/ProductAdded/types/pricing"
import ProductVersionMenu from "@/screens/Products/components/ProductVersionMenu"
import { formatLeadTimeDisplay } from "@/screens/Products/utils"

interface PurchaseOrderProductTableColumnsProps {
  onOpenPriceDialog?: (row: Row<OrderLine>, pricingUom: UnitOfMeasurement) => void
  isReadOnly?: boolean
  showCompanyName?: boolean
  order?: Order
  displayProductVersion?: boolean
  onProductVersionChange?: (
    orderLine: Row<Omit<PricingOrderLine, "orderLineId">>,
    versionInfo: ProductVersionInfo
  ) => void
}

export const getPurchaseOrderProductTableColumns = (
  {
    onOpenPriceDialog,
    isReadOnly,
    showCompanyName,
    displayProductVersion,
    onProductVersionChange,
    order,
  }: PurchaseOrderProductTableColumnsProps
  // TODO: Investigate how to avoid a lot of type coercion below
): ColumnDef<Omit<PricingOrderLine, "orderLineId">>[] => {
  const handleVersionUpdate = (row: Row<Omit<PricingOrderLine, "orderLineId">>, versionInfo: ProductVersionInfo) => {
    onProductVersionChange?.(row, versionInfo)
  }

  return [
    {
      header: "Product ID",
      accessorKey: "productId",
      cell: ({ row }) => {
        const line = row.original as OrderLine
        const product = line.product
        const productVersionsFilteredByLocations = product?.versions?.filter((el) => {
          return (
            !product?.locationsAssociations?.[0]?.shipping?.locationId ||
            el.shippingLocationId === product?.locationsAssociations?.[0]?.shipping?.locationId
          )
        })
        return (
          <Box className='flex flex-col'>
            <Box className='flex items-center gap-x-4'>{product && <ProductStatusCell product={product} />}</Box>
            <Box className='mt-1'>
              {displayProductVersion && (
                <ProductVersionMenu
                  productVersions={productVersionsFilteredByLocations || []}
                  productVersionId={line.productVersionId as number}
                  onSelectProductVersion={(versionInfo) => handleVersionUpdate(row, versionInfo)}
                />
              )}
            </Box>
          </Box>
        )
      },
      size: 80,
    },
    ...(showCompanyName
      ? [
          {
            header: "Company Name",
            accessorKey: "companyName",
            cell: ({ row }: CellContext<Omit<PricingOrderLine, "orderLineId">, unknown>) => (
              <span>{row.original.product?.company?.name || "-"}</span>
            ),
          },
        ]
      : []),
    {
      header: "Description",
      cell: ({ row }) => (
        <Box className='gap-2'>
          <div className='text-wrap'>Akrochem: {row.original.product?.companyProductDescription}</div>
          <div className='text-wrap'>Supplier: {row.original.product?.vendorDescription}</div>
        </Box>
      ),
      size: 120,
    },
    {
      header: "Lead Time",
      accessorKey: "leadTimeInDays",
      cell: ({ row }) => {
        const displayUom = row.original.product?.leadTimeDisplayUnits || LeadTimeUnits.Days
        const leadTime = formatLeadTimeDisplay(row.original.product?.leadTimeInDays, displayUom)
        return <Chip label={leadTime} variant='outlined' className='h-[24px] rounded bg-primary-200 px-2' />
      },
      size: 80,
    },
    {
      header: "Order Quantity",
      cell: ({ row }) =>
        isReadOnly ? (
          <OrderQuantityColumnReadOnly row={row as unknown as Row<OrderLine>} />
        ) : (
          <OrderQuantityColumn row={row as unknown as Row<OrderLine>} />
        ),
      size: 220,
    },
    {
      header: "Price/UoM",
      accessorKey: "pallets",
      cell: ({ row }) =>
        isReadOnly ? (
          <PriceUomColumnReadOnly row={row as unknown as Row<OrderLine>} />
        ) : (
          <PriceUomColumn row={row as unknown as Row<OrderLine>} onOpenPriceDialog={onOpenPriceDialog} />
        ),
      size: 100,
    },
    ...(order
      ? [
          {
            header: "Test Results",
            accessorKey: "testResults",
            cell: ({ row }: CellContext<Omit<PricingOrderLine, "orderLineId">, unknown>) => {
              const productTests = order?.productTests?.filter((test) => test.productId === row.original.productId)
              const withTestResults = productTests?.some((test) => !!test?.results?.length)

              return withTestResults ? (
                <CheckCircleFilledRounded className='text-tertiary-green-500' />
              ) : (
                <DoNotDisturbOnFilledRounded className='text-gray-500' />
              )
            },
            enableSorting: false,
          },
        ]
      : []),
    ...(isReadOnly
      ? []
      : [
          {
            header: "",
            accessorKey: "name",
            cell: ({ row }: CellContext<Omit<PricingOrderLine, "orderLineId">, unknown>) => (
              <ActionsCell row={row as unknown as Row<OrderLine>} />
            ),
            size: 50,
          },
        ]),
  ]
}
